import styles from "./styles";
import { TextField } from "../text-field";
import { Select } from "../select";
import { Button } from "@asosteam/asos-web-component-library-button";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ReCaptcha from "react-google-recaptcha";
import { itemCategoryOptions, repairTypeOptions } from "../../options";
import { isValidEmailAddress } from "../../utilities/validators";
import { submitRepairRequest } from "../../utilities/submit-repair-request";
import FormResponse from "../form-response";
import { name as nameValidation } from "../../../../../functions/SubmitRepairRequest/validation.json";

const isServerError = (error) => error?.status >= 500;

const getNameError = (name) => {
  if (!name.trim()) {
    return "required";
  }
  const length = name.trim().length;
  return length >= nameValidation.minLength &&
    length <= nameValidation.maxLength
    ? false
    : "invalid";
};

const nameErrorMessage = (error) =>
  error === "invalid"
    ? "Veuillez saisir un nom compris entre 2 et 100 caractères"
    : "Veuillez saisir votre nom";

const emailErrorMessage = (error) =>
  error === "invalid"
    ? "Cette adresse e-mail ne semble pas valide. Veuillez la saisir à nouveau."
    : "Veuillez saisir votre adresse e-mail";

const toSelectItems = (options, placeholderText) => [
  { value: "", text: placeholderText },
  ...options.map((option) => ({ value: option.id, text: option.displayName })),
];

const getEmailError = (email) => {
  if (!email) {
    return "required";
  }
  return isValidEmailAddress(email) ? false : "invalid";
};

export default function FormBody({
  recaptchaSiteKey,
  setFormStatus,
  ignoreCaptcha,
}) {
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(ignoreCaptcha);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [itemCategory, setItemCategory] = useState("");
  const [repairType, setRepairType] = useState("");

  const [validationErrors, setValidationErrors] = useState({
    name: false,
    email: false,
    itemCategory: false,
    repairType: false,
  });

  useEffect(() => {
    setHasErrors(Object.values(validationErrors).some((item) => item));
  }, [validationErrors]);

  const onSubmit = async () => {
    setSubmitInProgress(true);
    await handleSubmit();
    setSubmitInProgress(false);
  };

  const handleSubmit = async () => {
    const validationErrorState = {
      name: getNameError(name),
      email: getEmailError(email),
      itemCategory: !itemCategory,
      repairType: !repairType,
      isCaptchaVerified: !isCaptchaVerified,
    };

    setValidationErrors(validationErrorState);
    const errorsPresent = Object.values(validationErrorState).some(
      (item) => item
    );

    setHasErrors(errorsPresent);

    if (!errorsPresent) {
      const locale = window.asos?.webContext?.getBrowseLanguage();
      const payload = {
        name,
        email,
        itemCategory,
        repairType,
        locale,
      };

      setHasSubmitError(false);

      try {
        const response = await submitRepairRequest(payload);
        if (response.status === 200) {
          setFormStatus("success");
        } else {
          setFormStatus("error");
        }
      } catch (e) {
        handleSubmitFailure(e);
      }
    }
  };

  const handleSubmitFailure = (error) => {
    if (isServerError(error)) {
      setHasSubmitError(true);
    } else {
      setFormStatus("error");
    }
  };

  return (
    <div className={styles.container}>
      <small>*Informations requises</small>
      <div className={styles.formControl}>
        <label htmlFor="name">*NOM</label>
        <TextField
          id="name"
          ariaLabel="NOM"
          initialValue={name}
          placeholder="par ex. Pierre Durand"
          onChange={(e) => {
            setName(e.target.value);
            setValidationErrors({
              ...validationErrors,
              name: getNameError(e.target.value),
            });
          }}
          hasError={!!validationErrors.name}
          errorMessage={nameErrorMessage(validationErrors.name)}
          required
        />
      </div>
      <div className={styles.formControl}>
        <label htmlFor="email">*ADRESSE E-MAIL</label>
        <TextField
          id="email"
          ariaLabel="ADRESSE E-MAIL"
          initialValue={email}
          placeholder="par ex. pierre.durand@example.fr"
          onChange={(e) => {
            setEmail(e.target.value);
            setValidationErrors({
              ...validationErrors,
              email: getEmailError(e.target.value),
            });
          }}
          hasError={!!validationErrors.email}
          errorMessage={emailErrorMessage(validationErrors.email)}
          description="Nous indiquerons cette adresse e-mail lors de la transmission de votre demande à notre partenaire de réparation."
          required
        />
      </div>
      <div className={styles.formControl}>
        <label htmlFor="itemCategory">*CATÉGORIE D&apos;ARTICLE</label>
        <Select
          id="itemCategory"
          items={toSelectItems(itemCategoryOptions, "Sélectionnez une option")}
          ariaLabel="CATÉGORIE D'ARTICLE"
          initialValue={itemCategory}
          onItemClick={(e) => {
            setItemCategory(e.target.value);
            setValidationErrors({
              ...validationErrors,
              itemCategory: !e.target.value,
            });
          }}
          required
          hasError={!!validationErrors.itemCategory}
          errorMessage="Veuillez sélectionner une catégorie d'article"
        />
      </div>
      <div className={styles.formControl}>
        <label htmlFor="repairType">*TYPE DE RÉPARATION</label>
        <Select
          id="repairType"
          items={toSelectItems(repairTypeOptions, "Sélectionnez une option")}
          ariaLabel="TYPE DE RÉPARATION"
          initialValue={repairType}
          onItemClick={(e) => {
            setRepairType(e.target.value);
            setValidationErrors({
              ...validationErrors,
              repairType: !e.target.value,
            });
          }}
          required
          hasError={!!validationErrors.repairType}
          errorMessage="Veuillez sélectionner un type de réparation"
        />
      </div>
      <div className={styles.formControl}>
        <div className={styles.privacyNote}>
          <svg
            className={styles.privacyNoteIcon}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 2a5 5 0 0 0-5 5v3H5v12h14V10h-2V7a5 5 0 0 0-5-5Zm3 8V7a3 3 0 1 0-6 0v3h6Zm-3 4a1.5 1.5 0 0 1 .75 2.8V19h-1.5v-2.2A1.5 1.5 0 0 1 12 14Z"
              fill="currentColor"
            />
          </svg>
          <p>
            Les informations que vous nous transmettez serviront uniquement à
            traiter votre demande de réparation et seront communiquées à notre
            partenaire de réparation.
          </p>
        </div>
      </div>
      <div className={styles.formControl}>
        <div className={styles.reCaptchaContainer}>
          <ReCaptcha
            sitekey={recaptchaSiteKey}
            hl="fr"
            onChange={() => {
              setIsCaptchaVerified(true);
              setValidationErrors({
                ...validationErrors,
                isCaptchaVerified: false,
              });
            }}
            onExpired={() => setIsCaptchaVerified(false)}
          />
        </div>
      </div>
      <div className={styles.formControl}>
        <Button
          role="button"
          onClick={onSubmit}
          disabled={!isCaptchaVerified || submitInProgress || hasErrors}
        >
          ENVOYER UNE DEMANDE
        </Button>
      </div>
      {hasSubmitError && (
        <FormResponse
          heading="Nous sommes désolés, nous n'avons pas pu envoyer votre demande"
          message="Un problème est survenu de notre côté et votre demande n'a pas été envoyée. Vos informations sont toujours là. Veuillez réessayer dans quelques instants."
        ></FormResponse>
      )}
    </div>
  );
}

FormBody.propTypes = {
  recaptchaSiteKey: PropTypes.string.isRequired,
  setFormStatus: PropTypes.func.isRequired,
  ignoreCaptcha: PropTypes.bool,
};

FormBody.defaultProps = {
  ignoreCaptcha: false,
};
